import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import MovieCard from "@/pages/home/components/MovieCard";
import { movies } from "@/mocks/movies";

const typeOptions = ["全部", "电影", "剧集", "动画", "纪录片"] as const;
const modeOptions = ["全部", "在线观看", "免费观看", "高清播放", "手机在线"] as const;

interface MovieLibraryProps {
  genreFilter: string;
  onGenreFilterChange: (genre: string) => void;
}

export default function MovieLibrary({ genreFilter, onGenreFilterChange }: MovieLibraryProps) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState<string>("全部");
  const [mode, setMode] = useState<string>("全部");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return movies.filter((m) => {
      const matchQuery =
        !q ||
        m.title.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q)) ||
        m.tags.some((t) => t.toLowerCase().includes(q));
      const matchType = type === "全部" || m.type === type;
      const matchMode = mode === "全部" || m.mode.includes(mode as never);
      const matchGenre =
        genreFilter === "全部" ||
        m.genres.includes(genreFilter) ||
        m.region === genreFilter ||
        m.type === genreFilter;
      return matchQuery && matchType && matchMode && matchGenre;
    });
  }, [query, type, mode, genreFilter]);

  const reset = () => {
    setQuery("");
    setType("全部");
    setMode("全部");
    onGenreFilterChange("全部");
  };

  const hasFilter = query.trim() !== "" || type !== "全部" || mode !== "全部" || genreFilter !== "全部";

  return (
    <section id="library" className="bg-background-100 py-16 md:py-24 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-400"></span>
                影片片单
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                按题材与播放方式筛选，快速定位想看的内容
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                下面是本站整理的在架片单条目。可以用搜索框按标题、题材或标签查找，也可以点选类型与播放方式，
                两种条件会同时生效。所有筛选只作用于本页已展示的内容。
              </p>
            </div>
            <div className="shrink-0 text-sm text-foreground-500">
              当前显示 <span className="font-heading text-2xl font-bold text-primary-600 mx-1">{filtered.length}</span> / {movies.length} 条
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base" aria-hidden="true"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索标题、题材或标签，例如：悬疑、太空、治愈"
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  aria-label="搜索影片"
                />
              </div>
              {hasFilter && (
                <button
                  onClick={reset}
                  className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置筛选
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-foreground-500 shrink-0">类型</span>
                <div className="flex flex-wrap gap-2">
                  {typeOptions.map((t) => (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      className={`px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        type === t
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-xs text-foreground-500 shrink-0">播放方式</span>
              <div className="flex flex-wrap gap-2">
                {modeOptions.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      mode === m
                        ? "bg-secondary-500 text-background-50"
                        : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {genreFilter !== "全部" && (
              <div className="mt-4 pt-4 border-t border-background-200 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">题材筛选：</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {genreFilter}
                  <button
                    onClick={() => onGenreFilterChange("全部")}
                    className="ml-0.5 hover:text-accent-950 cursor-pointer"
                    aria-label="清除题材筛选"
                  >
                    <i className="ri-close-line" aria-hidden="true"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
        </Reveal>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((movie, idx) => (
              <Reveal key={movie.id} delay={Math.min(idx, 4) * 60}>
                <MovieCard movie={movie} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-8 bg-background-50 rounded-lg border border-dashed border-background-300 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-foreground-600">没有符合当前条件的内容</p>
            <p className="mt-1 text-sm text-foreground-400">试试更换题材关键词，或点“重置筛选”回到全部片单。</p>
            <button
              onClick={reset}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              查看全部片单
            </button>
          </div>
        )}
      </div>
    </section>
  );
}